import type { ImplementedCopilotFeatures, CopilotSettingsStore } from "owa-copilot-settings-store";
import { getApplicationSettings } from "owa-application-settings";
import isCopilotFeatureEnabled from "./isCopilotFeatureEnabled";
import createStoreCheckForGenericEnablement from "./createStoreCheckForGenericEnablement";
import type { MailboxInfo } from "owa-client-types";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import type CalendarEvent from "owa-calendar-types/lib/types/CalendarEvent";
import { isEventDraftAssistantEnabledForExistingEvents, isEventDraftAssistantEnabled, } from "./isEventDraftAssistantEnabled";

type GetAgendaScenarios = Extract<ImplementedCopilotFeatures, "AgendaGeneration">;

const isIntelligentDraftAgendaEnabled: (scenario: "AgendaGeneration", mailboxInfo: MailboxInfo, skipLanguageCheck?: boolean, shouldIgnoreUserPreferences?: boolean) => boolean | undefined = 
// eslint-disable-next-line owa-custom-rules/no-side-effects -- BASELINE, DO NOT COPY-PASTE
createStoreCheckForGenericEnablement<GetAgendaScenarios, boolean>("AgendaGeneration", isIntelligentDraftAgendaEnabledInternal);

export default isIntelligentDraftAgendaEnabled;

function isIntelligentDraftAgendaEnabledInternal(store: CopilotSettingsStore, _language: string, scenario: GetAgendaScenarios, mailboxInfo: MailboxInfo, skipLanguageCheck?: boolean, skipGroupMailboxCheck?: boolean): boolean {
    // Feature is not supported for Group Mailboxes
    if (!skipGroupMailboxCheck && mailboxInfo.type === "GroupMailbox") {
        return false;
    }
    
    if (store.isEnabled) {
        const featureEnabled = store.featureInfo.get(scenario);
        if (featureEnabled?.isEnabled) {
            const appSettings = getApplicationSettings("Copilot", mailboxInfo);
            return (!!isCopilotFeatureEnabled(scenario, mailboxInfo, skipLanguageCheck ?? appSettings.enableMultiLanguage) &&
                isGlobalFeatureEnabled("time-copilot-draft-intelligent-agenda") &&
                appSettings.calendarElaborateEnabled // This is controlled through the Killswitch ECS here: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            );
        }
    }
    return false;
}

/**
 * Checks if Intelligent Draft Agenda (AgendaGeneration) is enabled for the given event.
 *
 * By default, this feature is only available for new events. However, with the
 * draft assistant feature flag enabled, it also supports existing events
 * being edited by the organizer.
 *
 * @param scenario - The scenario to check (should be 'AgendaGeneration').
 * @param mailboxInfo - The mailbox information.
 * @param event - The calendar event to check. For new events, event.ItemId should be undefined.
 * @param skipLanguageCheck - Optional flag to skip language validation.
 * @param shouldIgnoreUserPreferences - Optional flag to ignore user preferences.
 * @param shouldUseCopilotScheduleFromMail - Optional flag indicating if this is a "schedule from mail" scenario.
 * @param isProtectiveAutoSaveOnly - Optional flag indicating this is a new event with a defined event.ItemId for adding attachments.
 * @returns true if the feature should be enabled for this event, false otherwise.
 */
export function isIntelligentDraftAgendaEnabledForEvent(scenario: "AgendaGeneration", mailboxInfo: MailboxInfo, event?: CalendarEvent, skipLanguageCheck?: boolean, shouldIgnoreUserPreferences?: boolean, shouldUseCopilotScheduleFromMail?: boolean, isProtectiveAutoSaveOnly?: boolean): boolean {
    // Draft Assistant (BizChat sidepane) fully replaces the inline agenda experience.
    // When active, DraftAgendaPlugin + DraftRewritePlugin handle drafting instead.
    if (isEventDraftAssistantEnabled(mailboxInfo)) {
        return false;
    }
    
    // Feature is only for new events, not existing events being edited
    // Exception: Allow for "schedule from mail" scenarios even if event has an ItemId
    // Also allow if the event was only auto-saved for protective purposes (e.g., attachments)
    // Also allow if draft assistant is enabled for existing events
    if (event &&
        event.ItemId !== undefined &&
        event.ItemId !== null &&
        !shouldUseCopilotScheduleFromMail &&
        !isProtectiveAutoSaveOnly &&
        !isEventDraftAssistantEnabledForExistingEvents()) {
        return false;
    }
    
    // Check the general feature enablement
    return (isIntelligentDraftAgendaEnabled(scenario, mailboxInfo, skipLanguageCheck, shouldIgnoreUserPreferences) ?? false);
}
